import {
  DashboardNewSignup,
  DashboardSubscriptionSummary,
} from "../../pages/api/dashboard";

import Link from "next/link";
import { formatDistanceToNow } from "date-fns";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { da, enUS, nb, sv } from "date-fns/locale";

interface AdminStatsProps {
  newSignups: DashboardNewSignup[];
  subscriptionSummary: DashboardSubscriptionSummary;
  systemStats: {
    totalPatients: number;
    totalAdvisors: number;
    messagesToday: number;
  };
}

const localeMap: Record<string, Locale> = {
  da: da,
  en: enUS,
  no: nb,
  sv: sv,
};

export default function AdminStats({
  newSignups,
  subscriptionSummary,
  systemStats,
}: AdminStatsProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const dateLocale = localeMap[router.locale || "en"] || enUS;

  return (
    <>
      <div className="row mb-3">
        <div className="col-12">
          <div className="alert alert-info mb-0 d-flex align-items-center">
            <strong className="me-2">{t("dashboard.adminSection")}</strong>
            <span className="text-muted">- {t("dashboard.onlyVisibleToAdmins")}</span>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-3">
        {/* System Overview */}
        <div className="col-md-4">
          <div className="card h-100">
            <div className="card-header">
              <h6 className="mb-0">{t("dashboard.systemOverview")}</h6>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between mb-2">
                <span>{t("dashboard.totalPatients")}</span>
                <strong>{systemStats.totalPatients}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>{t("dashboard.totalAdvisors")}</span>
                <strong>{systemStats.totalAdvisors}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span>{t("dashboard.messagesToday")}</span>
                <strong>{systemStats.messagesToday}</strong>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Summary */}
        <div className="col-md-4">
          <div className="card h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">{t("dashboard.subscriptions")}</h6>
              <Link
                href="/subscriptions"
                className="btn btn-sm btn-outline-primary"
              >
                {t("dashboard.seeAll")}
              </Link>
            </div>
            <div className="card-body">
              <div className="text-center mb-3">
                <div className="fs-2 fw-bold text-success">
                  {subscriptionSummary.totalMRR.toLocaleString(router.locale || "en")}{" "}
                  {subscriptionSummary.currency}
                </div>
                <small className="text-muted">{t("dashboard.monthlyRevenueMRR")}</small>
              </div>
              <div className="d-flex justify-content-between">
                <span>{t("dashboard.activeSubscriptions")}</span>
                <strong>{subscriptionSummary.activeCount}</strong>
              </div>
            </div>
          </div>
        </div>

        {/* New Signups */}
        <div className="col-md-4">
          <div className="card h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                {t("dashboard.newUsers7Days")}{" "}
                <span className="badge bg-success">{newSignups.length}</span>
              </h6>
              <Link href="/users" className="btn btn-sm btn-outline-primary">
                {t("dashboard.seeAll")}
              </Link>
            </div>
            <div className="card-body p-0">
              {newSignups.length === 0 ? (
                <p className="text-muted p-3 mb-0">
                  {t("dashboard.noNewSignupsLast7Days")}
                </p>
              ) : (
                <ul className="list-group list-group-flush">
                  {newSignups.slice(0, 5).map((signup) => (
                    <li
                      key={signup.id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <div className="fw-bold">
                          {signup.name || t("dashboard.unnamed")}
                        </div>
                        <small className="text-muted">{signup.email}</small>
                      </div>
                      <small className="text-muted">
                        {formatDistanceToNow(new Date(signup.createdAt), {
                          addSuffix: true,
                          locale: dateLocale,
                        })}
                      </small>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}


